import { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { usePageMeta } from "../hooks/usePageMeta";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../lib/motion";
import { SITE } from "../config/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERCORSI_OPTIONS = [
  "Diplomi",
  "Lauree",
  "Formazione professionale",
  "Certificazioni linguistiche e informatiche",
  "Altro / Orientamento generale",
];

const initialForm = { nome: "", telefono: "", email: "", percorso: "", messaggio: "" };

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3.5 text-sm text-navy placeholder:text-slate-400 outline-none transition-all duration-200 focus:border-azure focus:bg-white focus:ring-4 focus:ring-[rgba(36,85,138,0.12)]";

export default function Contatti() {
  usePageMeta(
    "Contatti",
    "Contatta Centro Studi Forma Futuro per un colloquio di orientamento: diplomi, lauree, formazione professionale e certificazioni."
  );
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      toast.success(res.data?.message || "Richiesta inviata con successo.");
      setForm(initialForm);
    } catch (err) {
      toast.error(
        "Non è stato possibile inviare la richiesta. Riprova tra poco o contattaci direttamente."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <main>
      <PageHeader
        testId="page-contatti"
        eyebrow="Contatti"
        title="Parliamo del tuo futuro."
        description="Compila il modulo o contattaci direttamente: ti risponderemo per fissare un colloquio di orientamento."
      />
      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <Reveal className="lg:col-span-5">
              <div className="bg-navy rounded-[1.6rem] p-8 lg:p-10 text-white h-full relative overflow-hidden grain">
                <div className="absolute -bottom-24 -right-24 w-72 h-72 rounded-full bg-azure-veil blur-3xl pointer-events-none" />
                <img src={SITE.logoWhite} alt="Forma Futuro" className="h-10 w-auto mb-8" />
                <p className="text-white/65 text-sm leading-relaxed max-w-sm">
                  Siamo a disposizione per un colloquio di orientamento: il
                  primo passo per costruire insieme il tuo percorso.
                </p>
                <ul className="mt-10 space-y-6">
                  <li className="flex items-start gap-4">
                    <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-white/10 text-sky-300 shrink-0">
                      <Phone size={19} />
                    </span>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-sky-300/80">Telefono</p>
                      <p data-testid="contact-info-phone" className="mt-1 text-sm font-semibold">{SITE.phone}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-white/10 text-sky-300 shrink-0">
                      <Mail size={19} />
                    </span>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-sky-300/80">Email</p>
                      <p data-testid="contact-info-email" className="mt-1 text-sm font-semibold break-all">{SITE.email}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-white/10 text-sky-300 shrink-0">
                      <MapPin size={19} />
                    </span>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-sky-300/80">Sede</p>
                      <p data-testid="contact-info-address" className="mt-1 text-sm font-semibold">{SITE.address}</p>
                    </div>
                  </li>
                </ul>
              </div>
            </Reveal>

            <Reveal delay={0.12} className="lg:col-span-7">
              <form
                data-testid="contact-form"
                onSubmit={onSubmit}
                className="bg-white rounded-[1.6rem] border border-slate-200 p-7 sm:p-9 lg:p-11 shadow-[0_30px_70px_-40px_rgba(8,42,89,0.3)]"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="nome" className="block text-xs font-bold uppercase tracking-[0.14em] text-navy/70 mb-2">
                      Nome e cognome *
                    </label>
                    <input
                      id="nome"
                      data-testid="input-nome"
                      type="text"
                      required
                      value={form.nome}
                      onChange={update("nome")}
                      placeholder="Il tuo nome"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label htmlFor="telefono" className="block text-xs font-bold uppercase tracking-[0.14em] text-navy/70 mb-2">
                      Telefono *
                    </label>
                    <input
                      id="telefono"
                      data-testid="input-telefono"
                      type="tel"
                      required
                      value={form.telefono}
                      onChange={update("telefono")}
                      placeholder="Il tuo numero"
                      className={inputCls}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="email" className="block text-xs font-bold uppercase tracking-[0.14em] text-navy/70 mb-2">
                      Email *
                    </label>
                    <input
                      id="email"
                      data-testid="input-email"
                      type="email"
                      required
                      value={form.email}
                      onChange={update("email")}
                      placeholder="La tua email"
                      className={inputCls}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="percorso" className="block text-xs font-bold uppercase tracking-[0.14em] text-navy/70 mb-2">
                      Percorso di interesse *
                    </label>
                    <select
                      id="percorso"
                      data-testid="select-percorso"
                      required
                      value={form.percorso}
                      onChange={update("percorso")}
                      className={`${inputCls} appearance-none ${form.percorso ? "" : "text-slate-400"}`}
                    >
                      <option value="" disabled>
                        Seleziona un percorso
                      </option>
                      {PERCORSI_OPTIONS.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="messaggio" className="block text-xs font-bold uppercase tracking-[0.14em] text-navy/70 mb-2">
                      Messaggio *
                    </label>
                    <textarea
                      id="messaggio"
                      data-testid="input-messaggio"
                      required
                      rows={5}
                      value={form.messaggio}
                      onChange={update("messaggio")}
                      placeholder="Raccontaci il tuo obiettivo o la tua domanda"
                      className={`${inputCls} resize-none`}
                    />
                  </div>
                </div>
                <label
                  htmlFor="privacy-consent"
                  className="mt-6 flex items-start gap-3 cursor-pointer"
                >
                  <input
                    id="privacy-consent"
                    data-testid="input-privacy-consent"
                    type="checkbox"
                    required
                    className="mt-0.5 w-4 h-4 rounded border-slate-300 accent-[#24558A] shrink-0"
                  />
                  <span className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                    Ho letto e accetto la{" "}
                    <a
                      href="/privacy"
                      data-testid="form-privacy-link"
                      className="font-bold text-azure underline underline-offset-2"
                    >
                      Privacy Policy
                    </a>{" "}
                    e acconsento al trattamento dei miei dati per essere
                    ricontattato. *
                  </span>
                </label>
                <button
                  type="submit"
                  data-testid="submit-contact-form"
                  disabled={sending}
                  className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-2.5 rounded-full bg-azure px-9 py-4 text-sm font-bold text-white shadow-[0_18px_38px_-14px_rgba(36,85,138,0.65)] transition-all duration-300 hover:bg-[#1C446E] hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
                >
                  {sending ? (
                    <>
                      <Loader2 size={17} className="animate-spin" />
                      Invio in corso…
                    </>
                  ) : (
                    <>
                      Invia richiesta
                      <Send size={16} />
                    </>
                  )}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
